import * as React from 'react';
import { reduxForm, InjectedFormProps, Field, getFormSyncErrors, change } from 'redux-form';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import { ErrorSummary, Modal, Button, WhiteButton } from 'components';
import { IModalAction } from 'models/modal';
import * as c from 'models/constants';
import { PortalInputField, PortalSelectMenu } from 'components/FormControls';
import { requestFailure } from 'redux/modules/image';
import { ButtonsSection, PortalPageSection } from 'components/PortalStructure';
import { IModalSizes } from 'components/Modal';
import { createDomainValueOptions, createLanguageDropdownValues } from 'helpers/common';
import { IDomainValue, ILanguage } from 'models/common';
import { IShopCardAction, IShopCardData } from 'models/shop/shopcard';
import { email, phoneNumber, required } from 'helpers/validationattributes';
import { getCountryCallingCodeDomainValues } from 'redux/modules/domainvalues';
import { IDomainValuesAction } from 'models/domainvalues/domainvalues';
import { addShopCustomer } from 'redux/modules/shop/shopcard';
import * as i18n from 'i18next';

interface IProps extends InjectedFormProps {
	type: string;
	modalSize?: IModalSizes['size'];
	shopCardData?: IShopCardData;

	currentFormValues: any;
	modalTitle: string;

	/* form props */
	submitFailed: boolean;
	handleSubmit: any;
	formErrors?: any;
	submit: (formName: string) => void;
	change: (field: string, value: any) => void;
	countryCallingCodes: IDomainValue[];
	languageDomainValues: ILanguage[];
	currentUserLanguageKey: number;
	reset?: (x: string) => void;

	/* functions props */
	requestFailure: (error: any) => void;
	t?: (key: string) => string;
	closeModal?: () => IModalAction;
	addShopCustomer: (
		shopId: string,
		name: string,
		callingCodeKey: number,
		phoneNumber: string,
		email: string,
		lastOrderDate: string,
		languageKey: number,
	) => IShopCardAction;
	getCountryCallingCodeDomainValues?: () => IDomainValuesAction;
	dispatch?: any;
}

const validateEmailPhone = (_value, values) => {
	if (values?.['phone-body'] || values?.email) {
		return undefined;
	} else {
		return i18n.t('Validation_Field_EmailOrPhone');
	}
};

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class AddCustomerModalForm extends React.Component<IProps> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.closeModal = this.closeModal.bind(this);
	}

	public componentDidMount() {
		if (this.props.currentUserLanguageKey) {
			this.props.change('languageKey', this.props.currentUserLanguageKey); // Set the customer language key to be the current user's language
		}
		this.props.getCountryCallingCodeDomainValues();
	}

	public componentWillUnmount() {
		this.props.dispatch(this.props.reset(c.FORM_NAME_ADD_CUSTOMER));
	}

	public onSubmit(/*form: any*/) {
		this.props.addShopCustomer(
			this.props.shopCardData.shopcard.id,
			this.props.currentFormValues['name'],
			this.props.currentFormValues['phone-prefix'],
			this.props.currentFormValues['phone-body'],
			this.props.currentFormValues['email'],
			new Date().toUTCString(),
			this.props.currentFormValues['languageKey'],
		);
		this.props.closeModal();
		this.setState({ uploadButtonClicked: false, editButtonClicked: false });
	}

	private closeModal() {
		this.props.closeModal();
		this.setState({ uploadButtonClicked: false, editButtonClicked: false });
	}

	private mapErrorKeyToMessage(key: string) {
		switch (key) {
			default:
				return key;
		}
	}

	/** --------------RENDER ----------------------- */
	public render() {
		const {
			handleSubmit,
			t,
			formErrors,
			submitFailed,
			modalTitle,
			modalSize,
			countryCallingCodes,
			languageDomainValues,
			currentFormValues,
			change,
		} = this.props;

		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', countryCallingCodes);
		const languages = createLanguageDropdownValues(languageDomainValues, t);

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const footer = (
			<ButtonsSection align="right">
				<div className="row">
					<div className="col-sm-12">
						<div className="floatRight">
							<WhiteButton onClick={this.closeModal}>{t('BSPortal_Common_Cancel')}</WhiteButton>
							<Button
								onClick={handleSubmit(() => {
									this.onSubmit();
								})}
							>
								{t('BSPortal_Common_Save')}
							</Button>
						</div>
					</div>
				</div>
			</ButtonsSection>
		);

		return (
			<Modal
				modalClass="modal-add-customer-wrapper"
				title={modalTitle}
				closeModal={this.closeModal}
				size={modalSize}
				footer={footer}
			>
				<form onSubmit={handleSubmit(this.onSubmit)} autoComplete="off">
					<PortalPageSection>
						<div className="row">
							<Field
								className="col-12"
								name="name"
								component={PortalInputField}
								type="text"
								label={t('Cart_CheckoutAddress_Label_NameAndSurname')}
								validate={[required]}
							/>
							<Field
								className="col-md-6 col-12"
								name="phone-prefix"
								component={PortalSelectMenu}
								type="text"
								items={callingCodes}
								multi={false}
								validate={[required]}
								isRequired={true}
								label={t('BSPortal_Common_Phone_Prefix')}
							/>
							<Field
								className="col-md-6 col-12"
								name="phone-body"
								component={PortalInputField}
								type="string"
								label={t('PhoneNumber_Label')}
								validate={[phoneNumber, validateEmailPhone]}
							/>
							<Field
								className="col-12"
								name="email"
								component={PortalInputField}
								type="string"
								label={t('User_UserPreferences_Label_Email')}
								validate={[email, validateEmailPhone]}
								onBlurAction={() => {
									// Trim before and after spaces
									if (currentFormValues?.email?.length) {
										change('email', currentFormValues.email.trim());
									}
								}}
							/>
							<Field
								className="col-12"
								name={'languageKey'}
								component={PortalSelectMenu}
								items={languages}
								label={t('User_UserPreferences_Label_Language')}
								isRequired={true} // Hide empty selection, doesn't enforce required!
								searchable={false}
							/>
						</div>
						<div>
							<ErrorSummary {...errorSummaryProps} />
						</div>
					</PortalPageSection>
				</form>
			</Modal>
		);
	}
}

function mapStateToProps(state: IStore) {
	const shopCardData = state.shopcard;
	let currentFormValues: any = null;
	if (state.form[c.FORM_NAME_ADD_CUSTOMER] && state.form[c.FORM_NAME_ADD_CUSTOMER].values) {
		currentFormValues = state.form[c.FORM_NAME_ADD_CUSTOMER].values;
	}
	return {
		shopCardData,
		currentFormValues,
		initialValues: {
			'phone-prefix': shopCardData?.shopcard?.callingCodeKey,
		},
		formErrors: getFormSyncErrors(c.FORM_NAME_ADD_CUSTOMER)(state),
		countryCallingCodes: state.domainValues.countryCallingCodes,
		languageDomainValues: state.domainValues.languages,
		currentUserLanguageKey: state.userLanguage.languageKey,
	};
}

let AddCustomerModal = reduxForm(
	{
		form: c.FORM_NAME_ADD_CUSTOMER,
		enableReinitialize: true,
	},
	mapStateToProps,
)(AddCustomerModalForm);

AddCustomerModal = connect(mapStateToProps, (dispatch) => ({
	change: (field: string, value: any) => dispatch(change(field, value)),
	requestFailure: (error) => dispatch(requestFailure(error)),
	getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
	addShopCustomer: (
		shopId: string,
		name: string,
		callingCodeKey: number,
		phoneNumber: string,
		email: string,
		lastOrderDate: string,
		languageKey: number,
	) => dispatch(addShopCustomer(shopId, name, callingCodeKey, phoneNumber, email, lastOrderDate, languageKey)),
	dispatch,
}))(AddCustomerModal);

export { AddCustomerModal };
